import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmployeeAvatar, Grid, LocalizedDateInput, Spinner, ButtonWithIcon, CategoryIcon, ChartIcon, CreateIcon, StatusWorkflowIcon, actionIconStyles } from '../../../shared/components';
import { formatTimeInputLabel, toDateInputValue } from '../../../shared/utils/localization';
import { stripRichTextToPlainText } from '../../../shared/utils/sanitizeRichTextHtml';
import { employeeService } from '../../admin/services/employeeService';
import { AI_TASK_ASSIGNEE, TaskAiRunStatus, TaskCategoryItem, TaskConversationLinkItem, TaskItem, TaskPriority, TaskStatus, TaskStatusDefinition, UpdateTaskRequest } from '../types/Task';
import { buildCategorySelectOptions, formatCategoryLabel } from '../utils/taskCategoryUtils';
import { taskService } from '../services/taskService';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { TaskCategoryModal } from './TaskCategoryModal';
import { TaskEditor } from './TaskEditor';
import { TaskChartModal } from './TaskChartModal';
import { StatusManagerModal } from './StatusManagerModal';
import { useStatusDraftManager } from '../hooks/useStatusDraftManager';
import {
  getAllowedTaskStatuses,
  getBlockedTaskStatusesByAssignee,
  getTaskStatusDefinition,
  getTaskStatusLabel,
  isTaskStatusCompleted,
  sortTaskStatuses,
} from '../utils/taskStatusTransitions';
import { getStatusBadgeStyle } from '../utils/statusManager';
import styles from './TaskList.module.css';

type SortField = 'title' | 'status' | 'priority' | 'dueDate';
type SortDirection = 'asc' | 'desc';

const priorityOrder: Record<TaskPriority, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

const priorityIconByLevel: Record<TaskPriority, string> = {
  low: '↓',
  medium: '↘',
  high: '↑',
  urgent: '⇈',
};

const priorityOptions: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const aiRunStatusLabel = (status: TaskAiRunStatus | undefined, t: (key: string, options?: { ns: string }) => string) => {
  switch (status) {
    case 'queued':
      return t('aiRunQueued', { ns: 'tasks' });
    case 'running':
      return t('aiRunRunning', { ns: 'tasks' });
    case 'succeeded':
      return t('aiRunSucceeded', { ns: 'tasks' });
    case 'failed':
      return t('aiRunFailed', { ns: 'tasks' });
    case 'needsReview':
      return t('aiRunNeedsReview', { ns: 'tasks' });
    default:
      return '';
  }
};
const VALID_ASSIGNEE_SCOPES = new Set(['all', 'my', 'employee', 'unassigned', 'ai']);

const stopRowActivation = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

type InlineTitleFieldProps = {
  taskId: string;
  title: string;
  disabled?: boolean;
  className?: string;
  onSave: (nextTitle: string) => void;
};

const InlineTitleField: React.FC<InlineTitleFieldProps> = ({
  taskId,
  title,
  disabled,
  className,
  onSave,
}) => {
  const [value, setValue] = useState(title);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setValue(title);
    }
  }, [taskId, title]);

  return (
    <input
      className={className}
      value={value}
      disabled={disabled}
      data-grid-interactive="true"
      onMouseDown={stopRowActivation}
      onClick={stopRowActivation}
      onFocus={() => {
        isEditingRef.current = true;
      }}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setValue(title);
          event.currentTarget.blur();
        }
      }}
      onBlur={(event) => {
        isEditingRef.current = false;
        const nextTitle = event.currentTarget.value.trim();
        if (!nextTitle || nextTitle === title) {
          setValue(title);
          return;
        }
        onSave(nextTitle);
      }}
    />
  );
};

const normalizePositiveInteger = (value: string | null, fallback: number): number => {
  const parsed = Number(value ?? '');
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.floor(parsed);
};

export const TaskList: React.FC = () => {
  const { t } = useTranslation(['tasks', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, auth } = useAuth();

  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TaskStatus>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | TaskPriority>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [assigneeScope, setAssigneeScope] = useState<'all' | 'my' | 'employee' | 'unassigned' | 'ai'>('all');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [employeeLookup, setEmployeeLookup] = useState<Record<string, { name: string; profileImageUrl?: string; colorPreference?: string }>>({});
  const [categoryOptions, setCategoryOptions] = useState<TaskCategoryItem[]>([]);
  const [statusOptions, setStatusOptions] = useState<TaskStatusDefinition[]>([]);
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState('');
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [savingTaskIds, setSavingTaskIds] = useState<string[]>([]);

  const canManageTasks = hasPermission([PermissionCodes.tasksManage]);
  const currentEmployeeId = auth?.employeeId || undefined;

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await taskService.listTasks({
        page,
        pageSize,
        searchText: searchText.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        priority: priorityFilter === 'all' ? undefined : priorityFilter,
        categoryId: categoryFilter === 'all' ? undefined : categoryFilter,
        assignedToUserId: assigneeScope === 'my'
          ? currentEmployeeId
          : assigneeScope === 'employee'
            ? (selectedEmployeeId || undefined)
            : undefined,
        assignedToAiOnly: assigneeScope === 'ai',
        unassignedOnly: assigneeScope === 'unassigned',
      });
      setTasks(result.items);
      setTotalCount(result.totalCount);
      setError(null);
    } catch {
      setError(t('failedToLoad', { ns: 'tasks' }));
    } finally {
      setLoading(false);
    }
  }, [assigneeScope, categoryFilter, currentEmployeeId, page, pageSize, priorityFilter, searchText, selectedEmployeeId, statusFilter, t]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextSearchText = params.get('q') ?? '';
    const nextStatusFilter = (params.get('status') as 'all' | TaskStatus | null) ?? 'all';
    const nextPriorityFilter = (params.get('priority') as 'all' | TaskPriority | null) ?? 'all';
    const nextCategoryFilter = params.get('category') ?? 'all';
    const nextAssigneeScope = VALID_ASSIGNEE_SCOPES.has(params.get('assignee') ?? '')
      ? (params.get('assignee') as 'all' | 'my' | 'employee' | 'unassigned' | 'ai')
      : 'all';
    const nextSelectedEmployeeId = nextAssigneeScope === 'employee' ? (params.get('employee') ?? '') : '';
    const nextPage = normalizePositiveInteger(params.get('page'), 1);
    const nextPageSize = normalizePositiveInteger(params.get('pageSize'), 25);

    setSearchText(current => current === nextSearchText ? current : nextSearchText);
    setSearchDraft(current => current === nextSearchText ? current : nextSearchText);
    setStatusFilter(current => current === nextStatusFilter ? current : nextStatusFilter);
    setPriorityFilter(current => current === nextPriorityFilter ? current : nextPriorityFilter);
    setCategoryFilter(current => current === nextCategoryFilter ? current : nextCategoryFilter);
    setAssigneeScope(current => current === nextAssigneeScope ? current : nextAssigneeScope);
    setSelectedEmployeeId(current => current === nextSelectedEmployeeId ? current : nextSelectedEmployeeId);
    setPage(current => current === nextPage ? current : nextPage);
    setPageSize(current => current === nextPageSize ? current : nextPageSize);
  }, [location.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalizedDraft = searchDraft.trim();
      const normalizedSearch = searchText.trim();

      if (normalizedDraft === normalizedSearch) {
        return;
      }

      setSearchText(searchDraft);
      setPage(1);
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchDraft, searchText]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams();

    if (currentParams.get('create') === '1' && canManageTasks) {
      nextParams.set('create', '1');
    }

    const normalizedSearch = searchText.trim();
    if (normalizedSearch) {
      nextParams.set('q', normalizedSearch);
    }

    if (statusFilter !== 'all') {
      nextParams.set('status', statusFilter);
    }

    if (priorityFilter !== 'all') {
      nextParams.set('priority', priorityFilter);
    }

    if (categoryFilter !== 'all') {
      nextParams.set('category', categoryFilter);
    }

    if (assigneeScope !== 'all') {
      nextParams.set('assignee', assigneeScope);
    }

    if (assigneeScope === 'employee' && selectedEmployeeId) {
      nextParams.set('employee', selectedEmployeeId);
    }

    if (page > 1) {
      nextParams.set('page', String(page));
    }

    if (pageSize !== 25) {
      nextParams.set('pageSize', String(pageSize));
    }

    const nextSearch = nextParams.toString() ? `?${nextParams.toString()}` : '';
    if (nextSearch !== location.search) {
      navigate({ pathname: '/tasks', search: nextSearch }, { replace: true });
    }
  }, [assigneeScope, canManageTasks, categoryFilter, location.search, navigate, page, pageSize, priorityFilter, searchText, selectedEmployeeId, statusFilter]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const employees = await employeeService.getEmployees({ isActive: true, sortBy: 'name', sortDir: 'asc', pageSize: 200 });
        const options = employees.map(employee => ({ id: employee.id, name: employee.name || employee.email }));
        const lookup = employees.reduce<Record<string, { name: string; profileImageUrl?: string; colorPreference?: string }>>((acc, employee) => {
          acc[employee.id] = {
            name: employee.name || employee.email,
            profileImageUrl: employee.profileImageUrl,
            colorPreference: employee.colorPreference,
          };
          return acc;
        }, {});
        setEmployeeOptions(options);
        setEmployeeLookup(lookup);
      } catch {
        setEmployeeOptions([]);
        setEmployeeLookup({});
      }
    };

    void loadEmployees();
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const categories = await taskService.listTaskCategories();
      setCategoryOptions(categories);
    } catch {
      setCategoryOptions([]);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const loadStatuses = useCallback(async () => {
    try {
      const statuses = await taskService.listTaskStatuses();
      setStatusOptions(sortTaskStatuses(statuses));
    } catch {
      setStatusOptions([]);
    }
  }, []);

  useEffect(() => {
    void loadStatuses();
  }, [loadStatuses]);

  const statusOrder = useMemo(() => {
    return sortTaskStatuses(statusOptions).reduce<Record<string, number>>((acc, status, index) => {
      acc[status.code] = index;
      return acc;
    }, {});
  }, [statusOptions]);

  const categorySelectOptions = useMemo(
    () => buildCategorySelectOptions(categoryOptions),
    [categoryOptions]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1' && canManageTasks) {
      setIsCreateModalOpen(true);
    }
  }, [canManageTasks, location.search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, priorityFilter, categoryFilter, assigneeScope, selectedEmployeeId]);

  const filtered = useMemo(() => {
    return [...tasks].sort((left, right) => {
      if (sortField === 'status') {
        const diff = (statusOrder[left.status] ?? Number.MAX_SAFE_INTEGER) - (statusOrder[right.status] ?? Number.MAX_SAFE_INTEGER);
        return sortDirection === 'asc' ? diff : -diff;
      }

      if (sortField === 'priority') {
        const diff = priorityOrder[left.priority] - priorityOrder[right.priority];
        return sortDirection === 'asc' ? diff : -diff;
      }

      if (sortField === 'dueDate') {
        const leftEmpty = !left.dueDate;
        const rightEmpty = !right.dueDate;
        if (leftEmpty !== rightEmpty) return leftEmpty ? 1 : -1;
        const leftValue = String(left.dueDate || '').toLowerCase();
        const rightValue = String(right.dueDate || '').toLowerCase();
        if (leftValue < rightValue) return sortDirection === 'asc' ? -1 : 1;
        if (leftValue > rightValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }

      const leftValue = String((left as any)[sortField] || '').toLowerCase();
      const rightValue = String((right as any)[sortField] || '').toLowerCase();
      if (leftValue < rightValue) return sortDirection === 'asc' ? -1 : 1;
      if (leftValue > rightValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [statusOrder, tasks, sortField, sortDirection]);

  const hasActiveFilters = useMemo(() => (
    searchText.trim().length > 0
    || statusFilter !== 'all'
    || priorityFilter !== 'all'
    || categoryFilter !== 'all'
    || assigneeScope !== 'all'
  ), [assigneeScope, categoryFilter, priorityFilter, searchText, statusFilter]);

  const clearFilters = useCallback(() => {
    setSearchDraft('');
    setSearchText('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
    setAssigneeScope('all');
    setSelectedEmployeeId('');
  }, []);

  const onSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
    const params = new URLSearchParams(location.search);
    params.set('create', '1');
    navigate({ pathname: '/tasks', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    const params = new URLSearchParams(location.search);
    params.delete('create');
    navigate({ pathname: '/tasks', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  const openCategoryModal = () => {
    setCategoryDraft('');
    setParentCategoryId('');
    setCategoryError(null);
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    if (categoryBusy) return;
    setIsCategoryModalOpen(false);
    setCategoryDraft('');
    setParentCategoryId('');
    setCategoryError(null);
  };

  const handleAddSubcategory = (nextParentCategoryId: string) => {
    setParentCategoryId(nextParentCategoryId);
    setCategoryDraft('');
    setCategoryError(null);
  };

  const handleCreateCategory = async () => {
    const name = categoryDraft.trim();
    if (!name) {
      setCategoryError(t('categoryNameRequired', { ns: 'tasks' }));
      return;
    }

    setCategoryBusy(true);
    setCategoryError(null);
    try {
      const created = await taskService.createTaskCategory(name, parentCategoryId || undefined);
      if (!created) {
        setCategoryError(t('failedToSave', { ns: 'tasks' }));
        return;
      }

      await loadCategories();
      setCategoryDraft('');
      setParentCategoryId('');
      if (categoryFilter === 'all') {
        // keep current selection
      } else {
        setCategoryFilter(created.id);
      }
    } catch (requestError) {
      setCategoryError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm(t('deleteCategoryConfirm', { ns: 'tasks' }))) return;

    setCategoryBusy(true);
    setCategoryError(null);
    try {
      await taskService.deleteTaskCategory(categoryId);
      await loadCategories();
      if (categoryFilter === categoryId) {
        setCategoryFilter('all');
      }
    } catch (requestError) {
      setCategoryError(requestError instanceof Error ? requestError.message : t('failedToDelete', { ns: 'tasks' }));
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleTaskCreated = () => {
    closeCreateModal();
    void loadTasks();
  };

  const openChartModal = () => setIsChartModalOpen(true);
  const closeChartModal = () => setIsChartModalOpen(false);

  const {
    isStatusModalOpen,
    statusModalBusy,
    statusModalError,
    statusDrafts,
    draggedStatusId,
    dragOverStatusId,
    dragOverPlacement,
    statusAnnouncement,
    statusAnnouncementId,
    savingStatusIds,
    statusAutosaveResults,
    newStatusDraft,
    statusPreviewDefinitions,
    statusValidation,
    statusDragHandleRefs,
    setNewStatusDraft,
    setNewStatusDraftDefault,
    setDraggedStatusId,
    setDragOverStatusId,
    setDragOverPlacement,
    openStatusModal,
    closeStatusModal,
    updateStatusDraft,
    setStatusDraftDefault,
    isStatusDraftDirty,
    handleCreateStatus,
    handleDeleteStatus,
    handleReorderStatus,
  } = useStatusDraftManager({
    statusOptions,
    setStatusOptions,
    statusFilter,
    setStatusFilter,
    loadTasks,
  });

  const assigneeFilterValue = useMemo(() => {
    if (assigneeScope === 'employee' && selectedEmployeeId) {
      return `employee:${selectedEmployeeId}`;
    }

    if (assigneeScope === 'employee') {
      return 'all';
    }

    return assigneeScope;
  }, [assigneeScope, selectedEmployeeId]);

  const handleAssigneeFilterChange = (value: string) => {
    if (value.startsWith('employee:')) {
      setAssigneeScope('employee');
      setSelectedEmployeeId(value.slice('employee:'.length));
      return;
    }

    setAssigneeScope(value as 'all' | 'my' | 'unassigned' | 'ai');
    setSelectedEmployeeId('');
  };

  const setTaskFieldLocally = useCallback((taskId: string, patch: Partial<TaskItem>) => {
    setTasks(prev => prev.map(task => (task.id === taskId ? { ...task, ...patch } : task)));
  }, []);

  const persistTaskUpdate = useCallback(async (taskId: string, request: UpdateTaskRequest) => {
    setSavingTaskIds(prev => (prev.includes(taskId) ? prev : [...prev, taskId]));
    try {
      const updated = await taskService.updateTask(taskId, request);
      if (updated) {
        setTasks(prev => prev.map(task => (task.id === taskId ? updated : task)));
      }
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
      void loadTasks();
    } finally {
      setSavingTaskIds(prev => prev.filter(id => id !== taskId));
    }
  }, [loadTasks, t]);

  const isTaskSaving = useCallback((taskId: string) => savingTaskIds.includes(taskId), [savingTaskIds]);

  const isOverdue = useCallback((task: TaskItem) => {
    if (!task.dueDate || isTaskStatusCompleted(task.status, statusOptions)) return false;
    return new Date(task.dueDate).getTime() < Date.now();
  }, [statusOptions]);

  const renderTaskKeyLink = (task: TaskItem) => (
    <button
      type="button"
      className={styles.taskKeyBadge}
      data-grid-interactive="true"
      onMouseDown={stopRowActivation}
      onClick={(event) => {
        stopRowActivation(event);
        navigate(`/tasks/${task.id}`);
      }}
    >
      {task.taskKey}
    </button>
  );

  const renderDueDateLabel = (task: TaskItem) => {
    if (!task.dueDate) return '-';

    const dateLabel = toDateInputValue(task.dueDate);
    const timeLabel = task.dueTime ? formatTimeInputLabel(task.dueTime) : null;
    return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
  };

  const getTaskRowClassName = useCallback((task: TaskItem) => {
    const classes = [styles.jiraRow];
    if (isTaskSaving(task.id)) classes.push(styles.rowSaving);
    if (isTaskStatusCompleted(task.status, statusOptions)) classes.push(styles.rowCompleted);
    else if (isOverdue(task)) classes.push(styles.rowOverdue);
    return classes.join(' ');
  }, [isOverdue, isTaskSaving, statusOptions]);

  const openTaskDetails = useCallback((task: TaskItem) => {
    navigate(`/tasks/${task.id}`);
  }, [navigate]);

  const getPrimaryConversationLink = (conversationLinks: TaskConversationLinkItem[]) => conversationLinks.find(link => link.isPrimary) ?? conversationLinks[0];

  const renderConversationSummary = (task: TaskItem) => {
    if (task.conversationLinks.length === 0) return null;

    const primaryConversation = getPrimaryConversationLink(task.conversationLinks);
    const linkedCountLabel = t('linkedConversationsCount', {
      ns: 'tasks',
      count: task.conversationLinks.length,
      defaultValue: task.conversationLinks.length === 1 ? '1 linked conversation' : `${task.conversationLinks.length} linked conversations`,
    });

    return (
      <span className={styles.tertiaryText}>
        {primaryConversation
          ? t('linkedConversationsSummary', {
            ns: 'tasks',
            countLabel: linkedCountLabel,
            conversationId: primaryConversation.conversationId.slice(0, 8),
            defaultValue: `${linkedCountLabel} · primary ${primaryConversation.conversationId.slice(0, 8)}`,
          })
          : linkedCountLabel}
      </span>
    );
  };

  if (loading && tasks.length === 0 && !error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loading', { ns: 'tasks' })} />
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{t('title', { ns: 'tasks' })}</h2>
          <p className={styles.subtitle}>
            {t('listSummary', { ns: 'tasks', count: totalCount, defaultValue: '{{count}} tasks' })}
          </p>
        </div>
        <div className={styles.headerActions}>
          {canManageTasks ? (
            <>
            <button className={`btn btn-secondary ${actionIconStyles.buttonWithIcon}`} type="button" onClick={openCategoryModal}>
              <ButtonWithIcon icon={<CategoryIcon />}>{t('manageCategories', { ns: 'tasks' })}</ButtonWithIcon>
            </button>
            <button className={`btn btn-secondary ${actionIconStyles.buttonWithIcon}`} type="button" onClick={openStatusModal}>
              <ButtonWithIcon icon={<StatusWorkflowIcon />}>{t('manageStatuses', { ns: 'tasks' })}</ButtonWithIcon>
            </button>
            </>
          ) : null}

          {canManageTasks ? (
            <button className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`} type="button" onClick={openCreateModal}>
              <ButtonWithIcon icon={<CreateIcon />}>{t('addTask', { ns: 'tasks' })}</ButtonWithIcon>
            </button>
          ) : null}

          <details className={styles.optionsMenu}>
            <summary
              className={`btn btn-secondary ${styles.optionsTrigger}`}
              title={t('options', { ns: 'common', defaultValue: 'Options' })}
              aria-label={t('options', { ns: 'common', defaultValue: 'Options' })}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <circle cx="3" cy="8" r="1.4" />
                <circle cx="8" cy="8" r="1.4" />
                <circle cx="13" cy="8" r="1.4" />
              </svg>
            </summary>
            <div className={styles.optionsList}>
              <button
                className={styles.optionsItem}
                type="button"
                onClick={(event) => {
                  const details = event.currentTarget.closest('details');
                  if (details) {
                    details.removeAttribute('open');
                  }
                  openChartModal();
                }}
              >
                <span className={actionIconStyles.tabLabel}>
                  <ChartIcon />
                  {t('viewAsChart', { ns: 'tasks', defaultValue: 'View as chart' })}
                </span>
              </button>
            </div>
          </details>
        </div>
      </div>

      <div className={styles.toolbarCard}>
        <div className={styles.toolbar}>
        <input
          className={`search-input ${styles.searchInput}`}
          placeholder={t('searchPlaceholder', { ns: 'tasks' })}
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
        />

        <select
          className={`text-input ${styles.selectInput}`}
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as 'all' | TaskStatus)}
        >
          <option value="all">{t('allStatuses', { ns: 'tasks' })}</option>
          {statusOptions.map(status => <option key={status.id} value={status.code}>{status.name}</option>)}
        </select>

        <select
          className={`text-input ${styles.selectInput}`}
          value={priorityFilter}
          onChange={(event) => setPriorityFilter(event.target.value as 'all' | TaskPriority)}
        >
          <option value="all">{t('allPriorities', { ns: 'tasks' })}</option>
          <option value="low">{t('priority.low', { ns: 'tasks' })}</option>
          <option value="medium">{t('priority.medium', { ns: 'tasks' })}</option>
          <option value="high">{t('priority.high', { ns: 'tasks' })}</option>
          <option value="urgent">{t('priority.urgent', { ns: 'tasks' })}</option>
        </select>

        <select
          className={`text-input ${styles.selectInput}`}
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <option value="all">{t('allCategories', { ns: 'tasks' })}</option>
          {categorySelectOptions.map(category => <option key={category.id} value={category.id}>{category.label}</option>)}
        </select>

        <div className={styles.assigneeFilterGroup}>
          <select
            className={`text-input ${styles.selectInput} ${styles.assigneeFilterSelect}`}
            value={assigneeFilterValue}
            aria-label={t('assigneeLabel', { ns: 'tasks' })}
            onChange={(event) => handleAssigneeFilterChange(event.target.value)}
          >
            <optgroup label={t('assigneeQuickFilters', { ns: 'tasks' })}>
              <option value="all">{t('allAssignees', { ns: 'tasks' })}</option>
              <option value="my" disabled={!currentEmployeeId}>{t('myTasks', { ns: 'tasks' })}</option>
              <option value="unassigned">{t('unassignedTasks', { ns: 'tasks' })}</option>
              <option value="ai">{t('aiAgentTasks', { ns: 'tasks' })}</option>
            </optgroup>
            <optgroup label={t('employeesGroup', { ns: 'tasks' })}>
              {employeeOptions.map(employee => <option key={employee.id} value={`employee:${employee.id}`}>{employee.name}</option>)}
            </optgroup>
          </select>
        </div>
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}

      {loading && tasks.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Spinner text={t('loading', { ns: 'tasks' })} />
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>
            {hasActiveFilters ? t('emptyFiltered', { ns: 'tasks', defaultValue: 'No tasks match your filters.' }) : t('empty', { ns: 'tasks' })}
          </p>
          {hasActiveFilters ? (
            <button className="btn btn-secondary" type="button" onClick={clearFilters}>
              {t('clearFilters', { ns: 'tasks', defaultValue: 'Clear filters' })}
            </button>
          ) : canManageTasks ? (
            <button className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`} type="button" onClick={openCreateModal}>
              <ButtonWithIcon icon={<CreateIcon />}>{t('addTask', { ns: 'tasks' })}</ButtonWithIcon>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <Grid
            columns={[
              {
                key: 'title',
                label: t('task', { ns: 'tasks' }),
                sortable: true,
                headerClassName: styles.titleColHeader,
                className: styles.titleColCell,
                render: (task: TaskItem) => {
                  const descriptionPreview = task.description ? stripRichTextToPlainText(task.description) : '';

                  return (
                  <div className={styles.cellStack}>
                    <div className={styles.primaryLine}>
                      {task.taskKey ? renderTaskKeyLink(task) : null}
                      {canManageTasks ? (
                        <InlineTitleField
                          taskId={task.id}
                          title={task.title}
                          className={styles.inlineTitleInput}
                          disabled={isTaskSaving(task.id)}
                          onSave={(nextTitle) => {
                            setTaskFieldLocally(task.id, { title: nextTitle });
                            void persistTaskUpdate(task.id, { title: nextTitle });
                          }}
                        />
                      ) : (
                        <span className={styles.primaryText}>{task.title}</span>
                      )}
                      {isTaskSaving(task.id) ? <span className={styles.savingDot} aria-hidden="true" /> : null}
                    </div>
                    {(task.labels && task.labels.length > 0) || descriptionPreview || task.conversationLinks.length > 0 ? (
                      <div className={styles.titleExtras}>
                        {task.labels && task.labels.length > 0 ? (
                          <div className={styles.labelRow}>
                            {task.labels.slice(0, 3).map(label => (
                              <span key={label.id} className={styles.labelChip}>{label.name}</span>
                            ))}
                            {task.labels.length > 3 ? (
                              <span className={styles.labelMore}>+{task.labels.length - 3}</span>
                            ) : null}
                          </div>
                        ) : null}
                        {descriptionPreview ? <span className={styles.secondaryText}>{descriptionPreview}</span> : null}
                        {renderConversationSummary(task)}
                      </div>
                    ) : null}
                  </div>
                  );
                },
              },
              {
                key: 'category',
                label: t('categoryLabel', { ns: 'tasks', defaultValue: 'Category' }),
                headerClassName: styles.metaColHeader,
                className: styles.metaColCell,
                render: (task: TaskItem) => (
                  <div className={styles.primaryLine}>
                    <span className={styles.columnText}>
                      {task.category ? formatCategoryLabel(task.category, categoryOptions) : t('none', { ns: 'tasks' })}
                    </span>
                  </div>
                ),
              },
              {
                key: 'status',
                label: t('statusLabel', { ns: 'tasks' }),
                sortable: true,
                headerClassName: styles.compactColHeader,
                className: styles.compactColCell,
                render: (task: TaskItem) => canManageTasks ? (
                  (() => {
                    const assigneeForStatus = task.assignedToAi ? AI_TASK_ASSIGNEE : task.assignedToUserId;
                    const availableStatuses = getAllowedTaskStatuses(task.status, statusOptions, assigneeForStatus);
                    const blockedStatuses = getBlockedTaskStatusesByAssignee(task.status, statusOptions, assigneeForStatus);
                    const blockedStatusLabel = blockedStatuses.length > 0
                      ? getTaskStatusLabel(blockedStatuses[0], statusOptions)
                      : null;
                    const statusColor = getTaskStatusDefinition(task.status, statusOptions)?.color;

                    return (
                      <div className={styles.primaryLine}>
                        <span
                          className={`${styles.badge} ${styles.statusBadgeSelect}`}
                          style={getStatusBadgeStyle(statusColor)}
                          data-grid-interactive="true"
                        >
                          <select
                            className={styles.inlineSelectBadge}
                            value={task.status}
                            disabled={isTaskSaving(task.id)}
                            title={blockedStatusLabel
                              ? t('assignBeforeRestrictedStatusHint', { ns: 'tasks', status: blockedStatusLabel })
                              : undefined}
                            onMouseDown={stopRowActivation}
                            onClick={stopRowActivation}
                            onChange={(event) => {
                              const status = event.target.value as TaskStatus;
                              setTaskFieldLocally(task.id, { status });
                              void persistTaskUpdate(task.id, { status });
                            }}
                          >
                            {availableStatuses.map(status => <option key={status} value={status}>{getTaskStatusLabel(status, statusOptions)}</option>)}
                          </select>
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <div className={styles.primaryLine}>
                    <span
                      className={styles.badge}
                      style={getStatusBadgeStyle(getTaskStatusDefinition(task.status, statusOptions)?.color)}
                    >
                      <span>{getTaskStatusLabel(task.status, statusOptions)}</span>
                    </span>
                  </div>
                ),
              },
              {
                key: 'priority',
                label: t('priorityLabel', { ns: 'tasks' }),
                sortable: true,
                headerClassName: styles.compactColHeader,
                className: styles.compactColCell,
                render: (task: TaskItem) => canManageTasks ? (
                  <div className={styles.primaryLine} data-grid-interactive="true">
                    <select
                      className={styles.inlineSelect}
                      value={task.priority}
                      disabled={isTaskSaving(task.id)}
                      onMouseDown={stopRowActivation}
                      onClick={stopRowActivation}
                      onChange={(event) => {
                        const priority = event.target.value as TaskPriority;
                        setTaskFieldLocally(task.id, { priority });
                        void persistTaskUpdate(task.id, { priority });
                      }}
                    >
                      {priorityOptions.map(priority => <option key={priority} value={priority}>{t(`priority.${priority}`, { ns: 'tasks' })}</option>)}
                    </select>
                  </div>
                ) : (
                  <div className={styles.primaryLine}>
                    <span className={styles.priorityCell}>
                      <span className={`${styles.priorityIcon} ${styles[`priorityIcon_${task.priority}`]}`}>{priorityIconByLevel[task.priority]}</span>
                      <span className={styles.priorityText}>{t(`priority.${task.priority}`, { ns: 'tasks' })}</span>
                    </span>
                  </div>
                ),
              },
              {
                key: 'dueDate',
                label: t('dueDate', { ns: 'tasks' }),
                sortable: true,
                headerClassName: styles.compactColHeader,
                className: styles.compactColCell,
                render: (task: TaskItem) => canManageTasks ? (
                  <div
                    className={`${styles.primaryLine} ${isOverdue(task) ? styles.dueDateCellOverdue : ''}`}
                    data-grid-interactive="true"
                  >
                    <LocalizedDateInput
                      className={styles.inlineDateInput}
                      displayFormat="input"
                      value={toDateInputValue(task.dueDate)}
                      disabled={isTaskSaving(task.id)}
                      fillWidth
                      onMouseDown={stopRowActivation}
                      onClick={stopRowActivation}
                      onValueChange={(dueDate) => {
                        if (!dueDate) return;
                        setTaskFieldLocally(task.id, { dueDate });
                        void persistTaskUpdate(task.id, { dueDate });
                      }}
                    />
                    {task.dueTime ? <span className={styles.dueTimeInline}>{formatTimeInputLabel(task.dueTime)}</span> : null}
                  </div>
                ) : (
                  <div className={styles.primaryLine}>
                    <span className={isOverdue(task) ? styles.dueDateOverdue : styles.dueDateText}>
                      {renderDueDateLabel(task)}
                    </span>
                  </div>
                ),
              },
              {
                key: 'assignee',
                label: t('assigneeLabel', { ns: 'tasks' }),
                headerClassName: styles.assigneeColHeader,
                className: styles.assigneeColCell,
                render: (task: TaskItem) => {
                  const assignee = task.assignedToUserId ? employeeLookup[task.assignedToUserId] : undefined;
                  const assigneeName = task.assignedToAi
                    ? t('aiAgent', { ns: 'tasks' })
                    : (assignee?.name || t('unknownAssignee', { ns: 'tasks' }));
                  const runLabel = aiRunStatusLabel(task.aiRunStatus, t);

                  if (canManageTasks) {
                    return (
                      <div className={styles.primaryLine} data-grid-interactive="true">
                        <div className={styles.assigneeEditor}>
                          {task.assignedToUserId || task.assignedToAi ? (
                            <EmployeeAvatar
                              name={assigneeName}
                              imageUrl={assignee?.profileImageUrl}
                              colorPreference={task.assignedToAi ? '#7C3AED' : assignee?.colorPreference}
                            size={24}
                          />
                        ) : (
                          <span className={styles.assigneeAvatarSlot} aria-hidden="true" />
                        )}
                        <select
                            className={styles.inlineSelect}
                            value={task.assignedToAi ? AI_TASK_ASSIGNEE : (task.assignedToUserId || EMPTY_GUID)}
                            disabled={isTaskSaving(task.id)}
                            onMouseDown={stopRowActivation}
                            onClick={stopRowActivation}
                            onChange={(event) => {
                              const value = event.target.value;
                              if (value === AI_TASK_ASSIGNEE) {
                                setTaskFieldLocally(task.id, { assignedToAi: true, assignedToUserId: undefined, aiRunStatus: task.assignedToAi ? task.aiRunStatus : 'queued' });
                                void persistTaskUpdate(task.id, { assignedToAi: true, assignedToUserId: EMPTY_GUID });
                                return;
                              }

                              setTaskFieldLocally(task.id, {
                                assignedToAi: false,
                                assignedToUserId: value === EMPTY_GUID ? undefined : value,
                                aiRunStatus: 'none',
                              });
                              void persistTaskUpdate(task.id, {
                                assignedToAi: false,
                                assignedToUserId: value,
                              });
                            }}
                          >
                            <option value={EMPTY_GUID}>{t('unassigned', { ns: 'tasks' })}</option>
                            <option value={AI_TASK_ASSIGNEE}>{t('aiAgent', { ns: 'tasks' })}</option>
                            {employeeOptions.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
                          </select>
                          {task.assignedToAi && runLabel ? <span className={styles.aiRunBadge}>{runLabel}</span> : null}
                          {task.assignedToAi && (task.aiRunStatus === 'failed' || task.aiRunStatus === 'needsReview') ? (
                            <button
                              type="button"
                              className={styles.aiRunAgain}
                              disabled={isTaskSaving(task.id)}
                              onMouseDown={stopRowActivation}
                              onClick={(event) => {
                                stopRowActivation(event);
                                setTaskFieldLocally(task.id, { aiRunStatus: 'queued' });
                                void persistTaskUpdate(task.id, { assignedToAi: true, requeueAiRun: true });
                              }}
                            >
                              {t('runAgain', { ns: 'tasks' })}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  }

                  if (!task.assignedToUserId && !task.assignedToAi) {
                    return (
                      <div className={styles.primaryLine}>
                        <span className={styles.unassignedText}>{t('unassigned', { ns: 'tasks' })}</span>
                      </div>
                    );
                  }

                  return (
                    <div className={styles.primaryLine}>
                      <span className={styles.assigneeChip}>
                        <EmployeeAvatar
                          name={assigneeName}
                          imageUrl={assignee?.profileImageUrl}
                          colorPreference={task.assignedToAi ? '#7C3AED' : assignee?.colorPreference}
                        size={24}
                      />
                      <span className={styles.assigneeName}>{assigneeName}</span>
                    </span>
                  </div>
                  );
                },
              },
              {
                key: 'createdBy',
                label: t('reportedByLabel', { ns: 'tasks', defaultValue: 'Reported By' }),
                headerClassName: styles.assigneeColHeader,
                className: styles.assigneeColCell,
                render: (task: TaskItem) => {
                  const reporter = employeeLookup[task.createdBy];
                  const reporterName = reporter?.name || t('unknownAssignee', { ns: 'tasks' });

                  if (!reporter) {
                    return (
                      <div className={styles.primaryLine}>
                        <span className={styles.unassignedText}>{reporterName}</span>
                      </div>
                    );
                  }

                  return (
                    <div className={styles.primaryLine}>
                      <span className={styles.assigneeChip}>
                        <EmployeeAvatar
                          name={reporterName}
                          imageUrl={reporter.profileImageUrl}
                          colorPreference={reporter.colorPreference}
                          size={24}
                        />
                        <span className={styles.assigneeName}>{reporterName}</span>
                      </span>
                    </div>
                  );
                },
              },
            ]}
            rows={filtered}
            rowKey={(task: TaskItem) => task.id}
            onRowClick={openTaskDetails}
            rowAlign="stretch"
            bodyCellClassName={styles.jiraDataCell}
            sortKey={sortField}
            sortDirection={sortDirection}
            onSort={(key) => onSort(key as SortField)}
            className={styles.jiraGrid}
            headerClassName={styles.jiraHeader}
            bodyClassName={styles.jiraBody}
            rowClassName={getTaskRowClassName}
          />

          <div className={styles.paginationBar}>
            <div className={styles.paginationInfo}>{t('paginationInfo', { ns: 'tasks', page, pageCount, totalCount })}</div>
            <div className={styles.paginationControls}>
              <label>
                <span className={styles.pageSizeLabel}>{t('pageSize', { ns: 'tasks' })}</span>
                <select
                  className={`text-input ${styles.pageSizeSelect}`}
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
              <button className="btn btn-secondary" type="button" disabled={page <= 1} onClick={() => setPage(current => Math.max(1, current - 1))}>
                {t('previous', { ns: 'tasks' })}
              </button>
              <button className="btn btn-secondary" type="button" disabled={page >= pageCount} onClick={() => setPage(current => Math.min(pageCount, current + 1))}>
                {t('next', { ns: 'tasks' })}
              </button>
            </div>
          </div>
        </>
      )}

      {isCreateModalOpen ? (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('addTask', { ns: 'tasks' })} onClick={closeCreateModal}>
          <div className={styles.modalCard} onClick={event => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{t('addTask', { ns: 'tasks' })}</h3>
              <button className={styles.modalCloseBtn} type="button" onClick={closeCreateModal} aria-label={t('close', { ns: 'common' })}>×</button>
            </div>
            <div className={styles.modalBody}>
              <TaskEditor
                embedded
                initialStatusOptions={statusOptions}
                initialCategoryOptions={categoryOptions}
                onSaved={handleTaskCreated}
                onCancel={closeCreateModal}
              />
            </div>
          </div>
        </div>
      ) : null}

      <TaskCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={closeCategoryModal}
        categoryDraft={categoryDraft}
        setCategoryDraft={setCategoryDraft}
        parentCategoryId={parentCategoryId}
        setParentCategoryId={setParentCategoryId}
        categoryBusy={categoryBusy}
        categoryError={categoryError}
        categoryOptions={categoryOptions}
        onCreateCategory={handleCreateCategory}
        onDeleteCategory={handleDeleteCategory}
        onAddSubcategory={handleAddSubcategory}
      />

      <TaskChartModal
        isOpen={isChartModalOpen}
        onClose={closeChartModal}
        tasks={filtered}
        employeeLookup={employeeLookup}
        statusOptions={statusOptions}
        categoryOptions={categoryOptions}
        onError={message => setError(message)}
      />

      <StatusManagerModal
        isOpen={isStatusModalOpen}
        statusModalBusy={statusModalBusy}
        savingStatusIds={savingStatusIds}
        statusAnnouncement={statusAnnouncement}
        statusAnnouncementId={statusAnnouncementId}
        statusValidation={statusValidation}
        newStatusDraft={newStatusDraft}
        setNewStatusDraft={setNewStatusDraft}
        setNewStatusDraftDefault={setNewStatusDraftDefault}
        statusOptions={statusOptions}
        handleCreateStatus={handleCreateStatus}
        statusModalError={statusModalError}
        statusDrafts={statusDrafts}
        draggedStatusId={draggedStatusId}
        dragOverStatusId={dragOverStatusId}
        dragOverPlacement={dragOverPlacement}
        setDraggedStatusId={setDraggedStatusId}
        setDragOverStatusId={setDragOverStatusId}
        setDragOverPlacement={setDragOverPlacement}
        handleReorderStatus={handleReorderStatus}
        handleDeleteStatus={handleDeleteStatus}
        statusPreviewDefinitions={statusPreviewDefinitions}
        isStatusDraftDirty={isStatusDraftDirty}
        statusAutosaveResults={statusAutosaveResults}
        statusDragHandleRefs={statusDragHandleRefs}
        updateStatusDraft={updateStatusDraft}
        setStatusDraftDefault={setStatusDraftDefault}
        closeStatusModal={closeStatusModal}
      />
    </div>
  );
};

export default TaskList;
